import { defineStore } from "pinia";
import SettingResource from "~/models/course-recommendations/Setting";
import AdminSettingResource from "~/models/Setting";
import SectionResource from "~/models/course-recommendations/Section";
import NoteResource from "~/models/course-recommendations/Note";

export const useCourseRecommendationsStore = defineStore("course-recommendations", {
  state: () => ({
      system_status: null,
      school_year: null,
      default_rec_type: null,
      level_rec_options: [],
      course_rec_options: [],
      course_rec_overrides: [],
      dept_rec_overrides: [],
      department_chairs: {},
      department_notes: {},
      all_notes: {},
      my_notes: {},
      teacher: null,
      current_section_teacher: null,
      loading_teacher_sections: false,
      teacher_sections: [],
      settings: [],
      settings_schema: {
        system_status: {
          label: "System Status",
          cast: String,
          field_type: "select",
          options: [
            { value: "open", label: "Open" },
            { value: "dept_chair_only", label: "Department Chairs Only" },
            { value: "closed", label: "Closed" },
          ],
        },
        default_rec_type: {
          label: "Default Recommendation Type",
          cast: String,
          field_type: "select",
          options: [
            { value: "Course", label: "Course" },
            { value: "Level", label: "Level" },
          ],
        },
        school_year: {
          label: "Current School Year",
          cast: Number,
          field_type: "input",
        },
        cc_emails: {
          label: "CC Emails To",
          cast: String,
          field_type: "input",
        },
        default_rec_options: {
          label: "Default Level Options",
          cast: String,
          field_type: "input",
        },
        dept_chairs: {
          label: "Department Chair Mapping",
          cast: String,
          field_type: "input",
        },
        options_by_dept: {
          label: "Department Options Override",
          cast: String,
          field_type: "input",
        },
        options_by_course: {
          label: "Course Option Overrides",
          cast: String,
          field_type: "input",
        },
        course_grading_periods: {
          label: "Allowed Grading Periods",
          cast: String,
          field_type: "input",
        },
        recommending_depts: {
          label: "Recommending Departments",
          cast: String,
          field_type: "input",
        },
        rec_exclude_courses: {
          label: "Excluded Courses",
          cast: String,
          field_type: "input",
        },
        rec_include_courses: {
          label: "Include Courses",
          cast: String,
          field_type: "input",
        },
      },
  }),
  getters: {
      canLoadSectionTeacher() {
        const normalize = (username) => (username || "").trim().toLowerCase();
        const sectionUsername = normalize(this.current_section_teacher?.username);
        const teacherUsername = normalize(this.teacher?.username);
        return Boolean(
          (this.userIsAdmin || this.userIsDeptChair) &&
          sectionUsername &&
          teacherUsername &&
          sectionUsername !== teacherUsername
        );
      },
      userIsAdmin: () => {
        const auth = useAuthStore();
        return (
          auth.user.hasRole("tech") ||
          auth.user.hasRole("course-recommendations-admin")
        );
      },
      userIsDeptChair: (state) => {
        const auth = useAuthStore();
        return (
          state.department_chairs[auth.user.username.toLowerCase()] !==
          undefined
        );
      },
      teacherIsDeptChair: (state) => {
        if (!state.teacher || !state.teacher.username) {
          return false;
        }
        return (
          state.department_chairs[state.teacher.username.toLowerCase()] !==
          undefined
        );
      },
      currentChairDepartment: (state) => {
        const auth = useAuthStore();
        // If logged in user is a DC, return their department.
        if (
          state.department_chairs[auth.user.username.toLowerCase()] !==
          undefined
        ) {
          return state.department_chairs[auth.user.username.toLowerCase()];
        }

        // If user isn't an admin, return false.
        if (
          !auth.user.hasRole("tech") &&
          !auth.user.hasRole("course-recommendations-admin")
        ) {
          return false;
        }

        // If impersonated teacher isn't a DC, return false.
        if (
          !state.teacher ||
          state.department_chairs[state.teacher.username.toLowerCase()] ===
            undefined
        ) {
          return false;
        }

        // Return impersonated user's department.
        return state.department_chairs[state.teacher.username.toLowerCase()];
      },
  },
  actions: {
      updateRelatedData(model, myKey, matchFn) {
        if (typeof this[myKey] != "object" || !Array.isArray(this[myKey])) {
          // Don't care about save.
          return;
        }

        const dataIndex = this[myKey].findIndex(matchFn);
        if (dataIndex == -1) {
          this[myKey].push(model);
        } else {
          this[myKey].splice(dataIndex, 1, model);
        }
      },
      removeRelatedData(model, myKey, matchFn) {
        if (typeof this[myKey] != "object" || !Array.isArray(this[myKey])) {
          // Don't care about save.
          return;
        }

        const dataIndex = this[myKey].findIndex(matchFn);
        if (dataIndex != -1) {
          this[myKey].splice(dataIndex, 1);
        }
      },
      getDepartmentNotes(deptId) {
        // Has a department been specified?
        if (!deptId) {
          return [];
        }
        return (
          this.department_notes.filter(
            (dept) => dept.note_type == `DEPT_${deptId.trim()}`
          ) || []
        );
      },
      savedNote(note) {
        const model = new NoteResource(note);

        const state_key =
          note.note_type === "teacher" ? "my_notes" : "department_notes";

        this.updateRelatedData(model, state_key, (i) => i.id == model.id);
        this.updateRelatedData(model, "all_notes", (i) => i.id == model.id);
        this[state_key].sort((a, b) => a.note.localeCompare(b.note));
        this.all_notes.sort((a, b) => a.note.localeCompare(b.note));
      },
      deletedNote(note) {
        const state_key =
          note.note_type === "teacher" ? "my_notes" : "department_notes";

        this.removeRelatedData(note, state_key, (i) => i.id == note.id);
        const model = new NoteResource(note);
        this.updateRelatedData(model, "all_notes", (i) => i.id == model.id);
      },
      getDepartmentNotesOld(deptId) {
        // Has a department been specified?
        if (!deptId) {
          return [];
        }

        // Have notes been specified for this school year?
        if (!this.department_notes[this.school_year]) {
          return [];
        }

        // Have notes been specified for this department?
        if (!this.department_notes[this.school_year]["DEPT_" + deptId.trim()]) {
          return [];
        }

        return (
          this.department_notes[this.school_year]["DEPT_" + deptId.trim()] || []
        );
      },
      setConfig(data) {
        this.system_status = data.system_status;
        this.school_year = data.school_year;
        this.default_rec_type = data.default_rec_type;
        this.level_rec_options = data.default_rec_options;
        this.course_rec_options = data.course_rec_options;
        this.course_rec_overrides = data.options_by_course;
        this.dept_rec_overrides = data.options_by_dept;
        this.department_chairs = data.dept_chairs;
        this.department_notes = data.department_notes.map(
          (n) => new NoteResource(n)
        );
        this.all_notes = data.all_notes.map((n) => new NoteResource(n));
        this.my_notes = data.my_notes.map((n) => new NoteResource(n));
      },
      resetConfig() {
        this.system_status = null;
        this.school_year = null;
        this.default_rec_type = null;
        this.level_rec_options = [];
        this.course_rec_options = [];
        this.course_rec_overrides = [];
        this.dept_rec_overrides = [];
        this.department_chairs = {};
        this.department_notes = {};
        this.all_notes = {};
        this.my_notes = {};
        this.teacher = null;
        this.current_section_teacher = null;
        this.teacher_sections = [];
      },
      async loadConfig() {
        try {
          const response = await SettingResource.custom(
            "settings/metadata"
          ).first();

          this.setConfig(response);
          this.setTeacher();

          return response;
        } catch (error) {
          console.log("ERROR Loading CourseRecommendation Config");
          const { $bugsnag } = useNuxtApp();
          $bugsnag.notify(error);
          if (!error.response) {
            console.log(error);
          } else {
            console.log(error.response.data.message);
          }
        }
      },
      async loadAdminSettings() {
        const response = await AdminSettingResource.where(
          "system",
          "rec"
        ).get();
        this.settings = response.data;
        return response;
      },
      setCurrentSectionTeacher(section) {
        const staff = section?.main_session?.primary_staff;
        const username = staff?.LOGIN_ID?.trim();
        this.current_section_teacher = username
          ? {
              section_key: section.SECTION_KEY,
              username,
              display_name: staff.display_name || username,
            }
          : null;
      },
      loadCurrentSectionTeacher() {
        if (!this.canLoadSectionTeacher || this.loading_teacher_sections) {
          return;
        }
        this.setTeacher(this.current_section_teacher);
      },
      setTeacher(teacher) {
        const auth = useAuthStore();

        if (!teacher) {
          teacher = auth.user;
        }
        this.teacher = teacher;
        this.loadTeacherRecSections(teacher.username);
      },
      async loadTeacherRecSections(teacherUsername) {
        this.loading_teacher_sections = true;

        try {
          const response = await SectionResource.with([
            "marking_periods",
            "student_recommendationsCount",
            "student_recommendations_finalizedCount",
          ])
            .where("SCHOOL_YEAR", this.school_year)
            .where("active_teacher", teacherUsername)
            .$get();

          if (response && response.length > 0) {
            response.sort((a, b) => {
              const sortA =
                `${a.display_marking_period} | ${a.period} | ${a.display_course_section}`.toLowerCase();
              const sortB =
                `${b.display_marking_period} | ${b.period} | ${b.display_course_section}`.toLowerCase();
              return sortA > sortB ? 1 : -1;
            });
          }
          this.loading_teacher_sections = false;
          this.teacher_sections = response;

          return response;
        } catch (error) {
          this.loading_teacher_sections = false;
          console.log("ERROR Loading CourseRecommendation Teacher Sections");
          const { $bugsnag } = useNuxtApp();
          $bugsnag.notify(error);
          if (!error.response) {
            console.log(error);
          } else {
            console.log(error.response.data.message);
          }
        }
      },
      savedTeacherSection(section) {
        const model = new SectionResource(section);

        this.updateRelatedData(
          model,
          "teacher_sections",
          (i) => i.SECTION_KEY == model.SECTION_KEY
        );
      },
  },
});
